import React, { useMemo, useState } from 'react';
import { useFormikContext, type FormikErrors } from 'formik';
import { RemoveCircle, Search } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import { type InterviewPanelMemberData } from './types';
import { useGetStaffInfoByNetID } from '../../../../../Nomination/graphql';
import { PAGE_TEXT } from '../../../../lang';
import {
  TextField,
  IconButton,
  type AlertDetails,
  Alert,
} from '../../../../../../components';

const pageText = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.PANEL_INFORMATION;

type PanelMemberInfoProps = {
  isViewMode: boolean;
  index: number;
  handleDeleteMember: () => void;
};

const InterviewPanelMemberTextContent: Array<{
  label: string;
  name: keyof InterviewPanelMemberData;
}> = [
  {
    label: pageText.MEMBER_INFO.NET_ID,
    name: 'netId',
  },
  {
    label: pageText.MEMBER_INFO.EMAIL,
    name: 'email',
  },
  {
    label: pageText.MEMBER_INFO.GIVEN_NAME,
    name: 'givenName',
  },
  {
    label: pageText.MEMBER_INFO.SURNAME,
    name: 'surname',
  },
  {
    label: pageText.MEMBER_INFO.DEPARTMENT,
    name: 'department',
  },
  {
    label: pageText.MEMBER_INFO.POST,
    name: 'post',
  },
  {
    label: pageText.MEMBER_INFO.RANK,
    name: 'rank',
  },
];
export const PanelMemberInfo: React.FunctionComponent<PanelMemberInfoProps> = (
  props: PanelMemberInfoProps,
) => {
  const { index: memberIndex, isViewMode, handleDeleteMember } = props;
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const { values, handleChange, handleBlur, errors, touched, setFieldValue } =
    useFormikContext<{ interviewPanelMembers: InterviewPanelMemberData[] }>();

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleSearchAndFill = async (): Promise<void> => {
    const searchId = values.interviewPanelMembers[memberIndex].netId ?? '';
    try {
      const res = await useGetStaffInfoByNetID(client, searchId);
      if (res !== null) {
        const fieldsToUpdate = {
          netId: searchId,
          department: res?.department ?? '',
          email: res?.email ?? '',
          givenName: res?.givenName ?? '',
          surname: res?.surname ?? '',
          jobTitle: res?.title ?? '', // the title from backend, which means post
          institution: 'The Hong Kong Polytechnic University',
          isPanelChair: memberIndex === 0,
        };

        for (const [field, value] of Object.entries(fieldsToUpdate)) {
          await setFieldValue(
            `interviewPanelMembers[${memberIndex}].${field}`,
            value,
          );
        }
      } else {
        setAlertDetails({
          alertContent: 'No matching academic staff found.',
          alertType: 'warning',
        });
        setOpenAlert(true);
      }
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleMemberInfoBox = useMemo((): React.ReactNode => {
    return (
      <>
        {InterviewPanelMemberTextContent.map((item, index) => {
          const fieldName = `interviewPanelMembers.${memberIndex}.${
            item.name as string
          }`;

          const memberErrors = errors.interviewPanelMembers as
            | Array<FormikErrors<InterviewPanelMemberData>>
            | undefined;
          const value = values.interviewPanelMembers[memberIndex][item.name];
          const isNetIdSearch = item.name === 'netId';
          return (
            <div
              key={index}
              className={isViewMode ? `col-span-1` : 'col-span-2'}
            >
              {isViewMode ? (
                <div className="text-sm !break-all">
                  <span className="font-semibold">{item.label}</span>:&nbsp;
                  {value}
                </div>
              ) : (
                <TextField
                  fullWidth
                  label={item.label}
                  name={fieldName}
                  value={value}
                  onChange={handleChange}
                  required
                  disabled={isViewMode}
                  InputLabelProps={{ shrink: true }}
                  onBlur={handleBlur}
                  error={
                    touched.interviewPanelMembers?.[memberIndex]?.[item.name] &&
                    Boolean(memberErrors?.[memberIndex]?.[item.name])
                  }
                  helperText={
                    touched.interviewPanelMembers?.[memberIndex]?.[item.name] &&
                    memberErrors?.[memberIndex]?.[item.name]
                  }
                  InputProps={{
                    endAdornment: isNetIdSearch ? (
                      <InputAdornment position="end">
                        <IconButton
                          icon={<Search />}
                          onClick={handleSearchAndFill}
                          disabled={
                            values.interviewPanelMembers[memberIndex].netId ===
                              undefined || isViewMode
                          }
                          color="primary"
                          title="Search by Net ID"
                        />
                      </InputAdornment>
                    ) : null,
                  }}
                />
              )}
            </div>
          );
        })}
      </>
    );
  }, [
    values.interviewPanelMembers[memberIndex],
    errors.interviewPanelMembers?.[memberIndex],
    touched.interviewPanelMembers?.[memberIndex],
  ]);

  return (
    <div className={isViewMode ? '' : 'pt-2 pb-6 px-6'}>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <div className="flex flex-row items-center mb-2 pl-4">
        {!isViewMode && (
          <h2 className="text-[#C5C5C5]">
            {pageText.MEMBER} {memberIndex + 1}
          </h2>
        )}
        {!isViewMode && memberIndex > 1 && (
          <IconButton
            icon={<RemoveCircle color="primary" />}
            onClick={handleDeleteMember}
          />
        )}
      </div>
      <div className="bg-white px-2">
        <div
          className={`grid gap-4 ${isViewMode ? 'grid-cols-[1fr,2fr,2fr,1fr] p-2' : 'grid-cols-4 px-2 py-4'}`}
        >
          {handleMemberInfoBox}
        </div>
      </div>
    </div>
  );
};
